import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router';
import { UserContextV2 } from '~/app';
import { isNotificationArrival } from '~/features/notifications/constants';
import { MARKET_PAGE_KEYS } from './routes';

// [MCV-1753] These pages follow a notification's `?responseId=` themselves (the opportunities
// machines' deep-link handling), so a forced same-page reload would only load them twice.
const SELF_DEEP_LINKED_PAGES: ReadonlySet<string> = new Set([
  MARKET_PAGE_KEYS.jobOpportunities,
  MARKET_PAGE_KEYS.serviceOpportunities,
  MARKET_PAGE_KEYS.b2bOpportunities,
]);

/**
 * [MCV-1537] Bridges react-router client-side navigation to the market machine.
 *
 * Each `/hlx/*` page loads its data in that page-state's `entry` action inside
 * `features/market/marketState.machine.ts`, and those states are reachable ONLY via
 * `ON_SET_PAGE`. Before this hook there were exactly two production senders of that
 * event to `marketRef`:
 *
 *   1. a one-shot `self.send` in `$_LOAD_EMPLOYER.invoke.onDone`, keyed off
 *      `window.location.pathname` — i.e. it only ever runs at boot, and
 *   2. the sidebar link click (`components/Layout/SideNav/HlxSideMenu.tsx:108`).
 *
 * Dashboard cards call bare `useNavigate()`, so the URL changed while the machine
 * stayed put: `$_JOB_LISTINGS_VIEW` / `$_MSAAS_LISTINGS_VIEW` were never entered,
 * `ON_LOAD` never reached the listing actors, and the page rendered an empty table
 * until a refresh re-ran the boot self-send against the new pathname.
 *
 * Fixing it here rather than in each card closes the whole class — browser
 * back/forward and `<Navigate>` redirects were equally broken — instead of adding
 * a fourth hand-rolled copy of the sidebar's send.
 *
 * [MCV-1753] A notification row's link carries `state.fromNotification`. Clicking it while
 * already on that page changes nothing in the path, so it used to be ignored (as was Guard 2's
 * "already there"), leaving the page showing what it held before the change that raised the alert.
 * That click now forces the page's load again, the market counterpart of `useVaultPageSync`'s forced
 * refresh. Only the click forces (`isNotificationArrival`): a back/forward onto an entry that kept
 * the state is a plain navigation. The opportunities pages are left to their own deep-link handling.
 */
export const useMarketPageSync = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const marketRef = UserContextV2.useSelector((state) => state.context.marketRef);
  const prevPathRef = useRef(location.pathname);
  const prevKeyRef = useRef(location.key);

  useEffect(() => {
    const currentPath = location.pathname;
    // `location.key` changes on every navigation, same-path ones included, and the effect also
    // re-runs when `marketRef` changes: only a new key can be a click.
    const isNewNavigation = location.key !== prevKeyRef.current;
    prevKeyRef.current = location.key;
    const forced = isNewNavigation && isNotificationArrival(location.state, navigationType);
    // Never fire on mount: boot is already handled by the machine's own self-send,
    // and firing here would race it.
    if (currentPath === prevPathRef.current && !forced) return;
    prevPathRef.current = currentPath;
    if (!marketRef) return;

    const snapshot = marketRef.getSnapshot();

    // Guard 1 — do NOT send while the employer/provider load invoke is in flight.
    // ON_SET_PAGE would exit $_LOAD_* and cancel that invoke, leaving employerUser
    // undefined forever; every $_*_VIEW entry action bails on !employerUser, so the
    // app would wedge permanently rather than just render late. This case needs no
    // handling of its own: the boot self-send reads the live pathname when it lands.
    if (
      snapshot.matches({ $_EMPLOYER: '$_LOAD_EMPLOYER' }) ||
      snapshot.matches({ $_PROVIDER: '$_LOAD_PROVIDER' })
    ) {
      return;
    }

    const page = currentPath.split('/').pop();
    if (!page) return;

    // Guard 2 — the sidebar already sends ON_SET_PAGE on click and assignCurrentPage
    // records it, so without this a sidebar navigation would fetch twice. Both entry
    // points now converge on one request per navigation.
    if (snapshot.context.currentPage === page) {
      if (!forced || SELF_DEEP_LINKED_PAGES.has(page)) return;
    }

    marketRef.send({ type: 'ON_SET_PAGE', payload: { page } });
  }, [location.pathname, location.key, location.state, navigationType, marketRef]);
};
